import SGrid from "../../components/grid/grid.component";
import {Button} from "primereact/button";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../providers/auth/auth-provider";
import {useMessage} from "../../providers/message/message-provider";
import {fetch} from "../../shared/api/v1";
import type {components} from "../../shared/api/v1/openapi.types";
import {getConnectionStatusLabel} from "../../constants/connection";
import {ConnectionExportActions} from '../../components/connection-export-actions/connection-export-actions';

export const ConnectionsList = () => {
  const navigate = useNavigate();
  const {token} = useAuth();
  const {showError} = useMessage();
  const [connections, setConnections] = useState<components['schemas']['Connection'][]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  useEffect(() => {
    let isMounted = true;
    const loadConnections = async () => {
      if (!token) {
        return;
      }
      setIsLoading(true);
      const response = await fetch.GET('/connections', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!isMounted) {
        return;
      }
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        showError({
          message: message ?? 'Ошибка загрузки заявок',
        });
        setConnections([]);
        setIsLoading(false);
        return;
      }
      setConnections(response.data ?? []);
      setIsLoading(false);
    };
    loadConnections();
    return () => {
      isMounted = false;
    };
  }, [showError, token]);

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <span className="text-xl text-900 font-bold">Заявления на технологическое присоединение</span>
      <Button
        icon="pi pi-plus"
        raised
        onClick={() => navigate('/cabinet/connection/new')}
      >
        Новое заявление
      </Button>
    </div>
  );

  return (
    <SGrid
      header={header}
      value={connections}
      loading={isLoading}
      rowHover
      rowClassName={() => 'cursor-pointer'}
      onRowClick={(event) => {
        const row = event.data as components['schemas']['Connection'];
        if (!row?.id) {
          return;
        }
        navigate(`/cabinet/connection/${row.id}`);
      }}
      emptyMessage={'Заявлений на технологическое присоединение нет'}
      paginator={true}
      rows={20}
      columns={[
        {
          field: 'id',
          header: '#',
        },
        {
          field: 'address',
          header: 'Адрес',
          body: (rowData: components['schemas']['Connection']) => rowData.address?.trim() || 'Адрес не указан',
        },
        {
          field: 'export',
          header: 'Скачать заявление',
          body: (rowData: components['schemas']['Connection']) => <ConnectionExportActions id={rowData.id} />,
        },
        {
          field: 'status',
          header: 'Статус',
          body: (rowData: components['schemas']['Connection']) =>
            getConnectionStatusLabel(rowData.status),
        },
      ]}
    />
  );
}
